import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../../shared/api';
import { Modal, FormField } from '../../ui/Modal';
import {
  IndividualServiceTypes,
  GroupServiceTypes,
  ProviderRoles,
  PlacesOfService,
  ServiceLabels,
  type ProviderRole,
  type ServiceType,
  type PlaceOfService,
} from '@census/shared';

interface ProviderFormState {
  firstName: string;
  lastName: string;
  email: string;
  cellPhone: string;
  externalRef: string;
  role: ProviderRole;
  serviceTypes: ServiceType[];
  placeOfService: PlaceOfService;
  telehealthCapable: boolean;
  maxDirectHoursPerWeek: number;
  locationId: string;
  notes: string;
}

const EMPTY: ProviderFormState = {
  firstName: '',
  lastName: '',
  email: '',
  cellPhone: '',
  externalRef: '',
  role: 'THERAPIST',
  serviceTypes: ['THERAPY'],
  placeOfService: 'OFFICE',
  telehealthCapable: true,
  maxDirectHoursPerWeek: 28,
  locationId: '',
  notes: '',
};

const ALL_SERVICES: ServiceType[] = [...GroupServiceTypes, ...IndividualServiceTypes];

function MoreBadge({ count, labels }: { count: number; labels: string[] }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  function show() {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    setPos({ top: rect.top - 8, left: rect.left });
  }

  return (
    <>
      <span
        ref={ref}
        onMouseEnter={show}
        onMouseLeave={() => setPos(null)}
        className="shrink-0 cursor-default rounded-full bg-ink-muted/10 px-2 py-0.5 text-xs font-medium text-ink-muted"
      >
        +{count} more
      </span>
      {pos &&
        createPortal(
          <div
            className="pointer-events-none fixed z-[9999] -translate-y-full rounded-md bg-gray-900 px-3 py-2 text-xs text-white shadow-xl"
            style={{ top: pos.top, left: pos.left }}
          >
            <div className="flex flex-col gap-0.5">
              {labels.map((l) => (
                <span key={l}>• {l}</span>
              ))}
            </div>
            <div
              className="absolute left-3 top-full h-0 w-0"
              style={{ borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '5px solid #111827' }}
            />
          </div>,
          document.body,
        )}
    </>
  );
}

export function ProviderForm({
  open,
  onClose,
  editing,
}: {
  open: boolean;
  onClose: () => void;
  editing?: any | null;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<ProviderFormState>(EMPTY);

  useEffect(() => {
    if (editing) {
      setForm({
        firstName: editing.firstName ?? '',
        lastName: editing.lastName ?? '',
        email: editing.email ?? '',
        cellPhone: editing.cellPhone ?? '',
        externalRef: editing.externalRef ?? '',
        role: editing.role ?? 'THERAPIST',
        serviceTypes: editing.serviceTypes ?? [],
        placeOfService: editing.placeOfService ?? 'OFFICE',
        telehealthCapable: editing.telehealthCapable ?? true,
        maxDirectHoursPerWeek: editing.maxDirectHoursPerWeek ?? 28,
        locationId: editing.locationId ?? '',
        notes: editing.notes ?? '',
      });
    } else {
      setForm(EMPTY);
    }
  }, [editing, open]);

  const { data: locations } = useQuery({
    queryKey: ['locations-active'],
    queryFn: () => api.get('/locations', { params: { activeOnly: 'true' } }).then((r) => r.data),
  });

  const save = useMutation({
    mutationFn: () => {
      const body: any = {
        firstName: form.firstName,
        lastName: form.lastName,
        role: form.role,
        serviceTypes: form.serviceTypes,
        placeOfService: form.placeOfService,
        telehealthCapable: form.telehealthCapable,
        maxDirectHoursPerWeek: form.maxDirectHoursPerWeek,
        locationId: form.locationId || null,
      };
      if (form.email) body.email = form.email;
      if (form.cellPhone) body.cellPhone = form.cellPhone;
      if (form.externalRef) body.externalRef = form.externalRef;
      if (form.notes) body.notes = form.notes;
      if (!editing) body.availability = []; // create requires the field; can edit later
      if (editing) return api.patch(`/providers/${editing.id}`, body).then((r) => r.data);
      return api.post('/providers', body).then((r) => r.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['providers'] });
      qc.invalidateQueries({ queryKey: ['providers-active'] });
      qc.invalidateQueries({ queryKey: ['resources-active'] });
      toast.success(editing ? 'Provider updated' : 'Provider created');
      onClose();
    },
  });

  const toggle = <T extends string>(arr: T[], v: T): T[] =>
    arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];

  // ── Searchable multi-select state ────────────────────────
  const [serviceSearch, setServiceSearch] = useState('');
  const [serviceOpen, setServiceOpen] = useState(false);
  const serviceRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!serviceOpen) setServiceSearch('');
  }, [serviceOpen]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (serviceRef.current && !serviceRef.current.contains(e.target as Node)) {
        setServiceOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filteredServices = ALL_SERVICES.filter((s) =>
    ServiceLabels[s].toLowerCase().includes(serviceSearch.toLowerCase()),
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit provider' : 'New provider'}
      subtitle="Create the provider here, then set weekly availability on the detail page."
      size="lg"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={!form.firstName || !form.lastName || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Create provider'}
          </button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label="First name" required>
          <input
            className="input"
            value={form.firstName}
            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
          />
        </FormField>
        <FormField label="Last name" required>
          <input
            className="input"
            value={form.lastName}
            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
          />
        </FormField>
        <FormField label="Email">
          <input
            type="email"
            className="input"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </FormField>
        <FormField label="Cell phone" hint="Used for SMS alerts (case-manager missed-appt notifications).">
          <input
            className="input"
            placeholder="e.g. (419) 555-0100 or +14195550100"
            value={form.cellPhone}
            onChange={(e) => setForm({ ...form, cellPhone: e.target.value })}
          />
        </FormField>
        <FormField
          label="EMR provider ID"
          hint="Paste from EMR. Lets the EMR pull this provider's calendar via /providers/external/{ref}/calendar to avoid double-booking."
        >
          <input
            className="input"
            placeholder="e.g. PRV-18A7C3"
            value={form.externalRef}
            onChange={(e) => setForm({ ...form, externalRef: e.target.value })}
          />
        </FormField>
        <FormField label="Role" required>
          <select
            className="input"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value as ProviderRole })}
          >
            {ProviderRoles.map((r) => (
              <option key={r} value={r}>
                {r.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Weekly hour cap" hint="Max direct-service hours/week">
          <input
            type="number"
            className="input"
            min={0}
            max={168}
            value={form.maxDirectHoursPerWeek}
            onChange={(e) =>
              setForm({ ...form, maxDirectHoursPerWeek: Number(e.target.value) })
            }
          />
        </FormField>
        <FormField label="Place of service" hint="Where this provider is based.">
          <select
            className="input"
            value={form.placeOfService}
            onChange={(e) =>
              setForm({ ...form, placeOfService: e.target.value as PlaceOfService })
            }
          >
            {PlacesOfService.map((p) => (
              <option key={p} value={p}>
                {p === 'HOME_COMMUNITY' ? 'Home / Community' : 'Office'}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Location" hint="The clinic location where this provider is based.">
          <select
            className="input"
            value={form.locationId}
            onChange={(e) => setForm({ ...form, locationId: e.target.value })}
          >
            <option value="">— None —</option>
            {(locations ?? []).map((l: any) => (
              <option key={l.id} value={l.id}>{l.city || l.name}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Telehealth capable" hint="Can deliver appointments via video.">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.telehealthCapable}
              onChange={(e) => setForm({ ...form, telehealthCapable: e.target.checked })}
            />
            <span>Yes — this provider can deliver telehealth visits</span>
          </label>
        </FormField>
        <FormField label="Services delivered" className="sm:col-span-2">
          <div ref={serviceRef} className="relative">
            {/* Trigger */}
            <button
              type="button"
              className="input flex min-h-[38px] w-full items-center gap-1.5 text-left"
              onClick={() => setServiceOpen((o) => !o)}
            >
              {form.serviceTypes.length === 0 ? (
                <span className="text-ink-muted text-sm">Select services…</span>
              ) : (
                <span className="flex flex-1 items-center gap-1.5 overflow-hidden">
                  {form.serviceTypes.slice(0, 2).map((s) => (
                    <span
                      key={s}
                      className="inline-flex shrink-0 items-center gap-1 rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-700"
                    >
                      {ServiceLabels[s]}
                      <span
                        role="button"
                        className="cursor-pointer opacity-60 hover:opacity-100"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          setForm({ ...form, serviceTypes: toggle(form.serviceTypes, s) });
                        }}
                      >
                        ×
                      </span>
                    </span>
                  ))}
                  {form.serviceTypes.length > 2 && (
                    <MoreBadge
                      count={form.serviceTypes.length - 2}
                      labels={form.serviceTypes.slice(2).map((s) => ServiceLabels[s])}
                    />
                  )}
                </span>
              )}
              <span className="ml-auto shrink-0 text-ink-muted">▾</span>
            </button>

            {/* Dropdown */}
            {serviceOpen && (
              <div className="absolute z-50 mt-1 w-full rounded-md border border-line bg-white shadow-lg">
                {/* Search input */}
                <div className="border-b border-line px-2 py-2">
                  <input
                    autoFocus
                    className="w-full rounded-md border border-line px-2 py-1.5 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                    placeholder="Search services…"
                    value={serviceSearch}
                    onChange={(e) => setServiceSearch(e.target.value)}
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                </div>
                {/* Options list */}
                <div className="max-h-52 overflow-y-auto p-1">
                  {filteredServices.length === 0 ? (
                    <p className="px-3 py-2 text-xs text-ink-muted">No services match</p>
                  ) : (
                    filteredServices.map((s) => {
                      const checked = form.serviceTypes.includes(s);
                      return (
                        <label
                          key={s}
                          className="flex cursor-pointer items-center gap-2.5 rounded px-2 py-1.5 text-sm hover:bg-surface-muted"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() =>
                              setForm({ ...form, serviceTypes: toggle(form.serviceTypes, s) })
                            }
                            className="h-4 w-4 accent-brand-600"
                          />
                          <span className={checked ? 'font-medium text-ink' : 'text-ink-subtle'}>
                            {ServiceLabels[s]}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
                {/* Footer: count + clear */}
                <div className="flex items-center justify-between border-t border-line px-3 py-1.5 text-xs text-ink-muted">
                  <span>{form.serviceTypes.length} selected</span>
                  {form.serviceTypes.length > 0 && (
                    <button
                      type="button"
                      className="text-rose-600 hover:underline"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setForm({ ...form, serviceTypes: [] })}
                    >
                      Clear all
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </FormField>
        <FormField label="Notes">
          <textarea
            className="input min-h-[80px]"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </FormField>
      </div>
    </Modal>
  );
}
